import { Component, type ErrorInfo, type ReactNode } from 'react';
import { trackException } from '../lib/telemetry';
import { CaliberMark } from './CaliberMark';

interface Props { children: ReactNode; }
interface State { hasError: boolean; }

// Catches React render-tree errors (which window.onerror doesn't reliably get),
// reports them to CloudWatch through /api/client-error (lib/telemetry.ts), and
// shows a minimal fallback instead of a blank white screen. Window errors and
// unhandled rejections are reported by lib/telemetry's own listeners; this
// covers the React-specific gap.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    trackException(error, {
      source: 'react-error-boundary',
      componentStack: info.componentStack ?? undefined,
    });
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    // THE FAILURE ON THE DESK (owner call 2026-10-12: "follow our dot grid design background, and our fonts … no
    // divider lines"): Caliber's mark, the words in the app's display face at a weight it loads, what to do in its
    // mono, one button; left aligned on the shell's own dot grid (.ofself-desk). Inline, with literal fallbacks: this
    // is the error path, so it must stand even if the app's stylesheet failed to load.
    const ink = 'rgb(var(--ofself-ink-rgb, 17 17 17))';
    const display = 'var(--ofself-font-display)';
    const mono = "'JetBrains Mono', var(--ofself-font-mono, ui-monospace, monospace)";
    return (
      <div role="alert" className="ofself-desk" style={{
        minHeight: '100vh', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', justifyContent: 'center',
        alignItems: 'flex-start', gap: 24, padding: 'clamp(16px, 8vw, 120px)', color: ink,
        backgroundColor: 'var(--ofself-desk-ground, var(--ofself-surface, #f5f5f5))',
      }}>
        <span style={{ width: 40, color: ink }}><CaliberMark /></span>
        <h1 style={{ margin: 0, fontFamily: display, fontWeight: 500, fontSize: 'clamp(40px, 7vw, 96px)', lineHeight: 0.95, letterSpacing: '-0.035em' }}>
          Something went wrong
        </h1>
        <button type="button" onClick={() => window.location.reload()} style={{
          marginTop: 8, minHeight: 36, padding: '0 16px', border: `1px solid ${ink}`, borderRadius: 0, background: ink,
          color: 'var(--ofself-desk-ground, var(--ofself-surface, #f5f5f5))', cursor: 'pointer',
          fontFamily: mono, fontWeight: 500, fontSize: 10.5, letterSpacing: '0.15em', textTransform: 'uppercase',
        }}>
          Reload
        </button>
      </div>
    );
  }
}
